/**
 * Errors are values across every layer of this app: nothing throws across a
 * boundary, callers branch on the discriminant and return early.
 */
import type { ErrorEnvelope, InUseBy } from "./domain.ts";
import type { Dictionary } from "../i18n/dictionary.ts";

export type ApiError =
  | { kind: "network"; message: string }
  | { kind: "decode"; message: string }
  | { kind: "server"; status: number; envelope: ErrorEnvelope };

export type Result<T> = { ok: true; value: T } | { ok: false; error: ApiError };

export const ok = <T>(value: T): Result<T> => ({ ok: true, value });

export const err = <T = never>(error: ApiError): Result<T> => ({ ok: false, error });

/** Maps a successful result to a new value, passing failures through untouched. */
export const mapResult = <T, U>(result: Result<T>, transform: (value: T) => U): Result<U> =>
  result.ok ? ok(transform(result.value)) : result;

const isEnvelope = (body: unknown): body is ErrorEnvelope => {
  if (typeof body !== "object" || body === null) return false;
  const { kind } = body as { kind?: unknown };
  if (kind === "not_found") {
    const { entity, id } = body as { entity?: unknown; id?: unknown };
    return typeof entity === "string" && typeof id === "string";
  }
  if (kind === "validation" || kind === "conflict") {
    return typeof (body as { message?: unknown }).message === "string";
  }
  if (kind === "in_use") {
    const { entity, id, referrers } = body as {
      entity?: unknown;
      id?: unknown;
      referrers?: unknown;
    };
    return typeof entity === "string" && typeof id === "string" && Array.isArray(referrers);
  }
  return kind === "unauthorized" || kind === "forbidden" || kind === "internal";
};

/**
 * Turns a failing response into an ApiError. A body that does not carry the
 * server's envelope is treated as an internal failure, which is what an
 * unrecognized shape means for the caller.
 */
export const serverError = (status: number, body: unknown): ApiError => ({
  kind: "server",
  status,
  envelope: isEnvelope(body) ? body : { kind: "internal" },
});

/** True when the credential was missing, wrong or dead, so the shell can re-ask for it. */
export const isUnauthorized = (error: ApiError): boolean =>
  error.kind === "server" && error.envelope.kind === "unauthorized";

/** True when the caller is signed in and may not do this. */
export const isForbidden = (error: ApiError): boolean =>
  error.kind === "server" && error.envelope.kind === "forbidden";

/** True when nothing is stored under the identity that was asked for. */
export const isNotFound = (error: ApiError): boolean =>
  error.kind === "server" && error.envelope.kind === "not_found";

/**
 * The entities blocking a delete, or undefined when the failure was something
 * else. The confirm dialog branches on this to offer the forced delete.
 */
export const inUseBy = (error: ApiError): InUseBy[] | undefined =>
  error.kind === "server" && error.envelope.kind === "in_use" ? error.envelope.referrers : undefined;

/** A readable sentence for any failure, in the reader's language. */
export const describeError = (dictionary: Dictionary, error: ApiError): string => {
  if (error.kind === "network") return `${dictionary.error.network}: ${error.message}`;
  if (error.kind === "decode") return `${dictionary.error.decode}: ${error.message}`;

  const { envelope } = error;
  if (envelope.kind === "not_found") {
    return `${dictionary.error.notFound}: ${envelope.entity} ${envelope.id}`;
  }
  if (envelope.kind === "validation") return `${dictionary.error.validation}: ${envelope.message}`;
  if (envelope.kind === "conflict") return `${dictionary.error.conflict}: ${envelope.message}`;
  if (envelope.kind === "in_use") {
    return `${dictionary.error.inUse}: ${envelope.entity} ${envelope.id}`;
  }
  if (envelope.kind === "unauthorized") return dictionary.error.unauthorized;
  if (envelope.kind === "forbidden") return dictionary.error.forbidden;
  return dictionary.error.internal;
};
